import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommissionReportsService } from '../../services/commission-reports.service';
import { NavalMatrizItem, CommissionReportsData } from '../../interfaces/commission-reports.interface';
import { forkJoin, Subscription } from 'rxjs';
import { ExerciseCalendar } from '../shared/exercise-calendar/exercise-calendar';
import { SelectedDnService } from '../../services/selected-dn.service';
import { ReportService } from '../../services/report.service';
import { ExportDocumentButton } from '../../export-document-button/export-document-button';
import { ReportPageContextService } from '../../services/report-page-context.service';
import { ExportDownloadModal } from '../export-download-modal/export-download-modal';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { runtimeLogger } from '../../utils/runtime-logger.util';

@Component({
  selector: 'app-comission-reports',
  standalone: true,
  imports: [
    CommonModule,
    NgxEchartsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    ExerciseCalendar,
    ExportDocumentButton,
    ExportDownloadModal
  ],
  templateUrl: './comission-reports.html',
  styleUrls: ['./comission-reports.scss']
})
export class ComissionReports implements OnInit, OnDestroy {
  private dnSubscription?: Subscription;

  @ViewChild('reportContainer') reportContainer!: ElementRef<HTMLElement>;

  isBrowser: boolean;
  selectedSegment: 'varejo' | 'frota' = 'varejo';

  // Dados das tabelas de Penetration (preenchidos pela API)
  varejoTableData: any[] = [];
  frotaTableData: any[] = [];

  tableColumns: string[] = [
    '0-39,9',
    '40-49,9',
    '50-54,9',
    '55-59,9',
    '60-64,9',
    '65-69,9',
    '70-74,9',
    '75-79,9',
    '80-84,9',
    '85-89,9',
    '90-99,9',
    '100-124,9',
    '125-149,9',
    '150-174,9',
    '175-199,9',
    '>200'
  ];

  // Índices vigentes (preenchidos pela API)
  varejoIndice = {
    top: { value: '' },
    bottom: { value: '' }
  };

  frotaIndice = {
    top: { value: '' },
    bottom: { value: '' }
  };

  // Gráficos de Performance (preenchidos pela API)
  varejoPerformanceChart: EChartsOption = {};
  frotaPerformanceChart: EChartsOption = {};

  // Dados do Período Atual (preenchidos pela API)
  varejoPeriodoAtual = {
    periodo: '',
    penetration: '',
    isr: ''
  };

  frotaPeriodoAtual = {
    periodo: '',
    penetration: '',
    isr: ''
  };

  // Dados do Período Anterior (Mês 1) - CORRIGIDO: SEPARADO PARA VAREJO E FROTA
  varejoPeriodoAnterior = {
    periodo: '',
    penetration: '',
    isr: ''
  };

  frotaPeriodoAnterior = {
    periodo: '',
    penetration: '',
    isr: ''
  };

  // Métricas de Penetration e ISR (preenchidas pela API)
  varejoMetrics: {
    penetration: Array<{ label: string; value: string; color: string }>;
    isr: Array<{ label: string; value: string; color: string }>;
  } = {
      penetration: [],
      isr: []
    };

  frotaMetrics: {
    penetration: Array<{ label: string; value: string; color: string }>;
    isr: Array<{ label: string; value: string; color: string }>;
  } = {
      penetration: [],
      isr: []
    };

  loading: boolean = true;
  reportsData?: CommissionReportsData;
  exercicioAtual?: number;
  exercicioSelecionado?: number;
  exercicioMismatchError: string | null = null;
  errorMessage: string | null = null;
  is404Error: boolean = false;
  private exerciciosTentados: number[] = [];

  // Datas de fechamento para os dois relatórios
  closedDateCommissionPerformance: string = '';
  closedDateMatrizIsr: string = '';
  availableExercicios: string[] = [];

  // NOVAS PROPRIEDADES PARA CONTROLE DE PERÍODO
  selectedYear: number | null = null;
  selectedMonth: number | null = null;

  showExportModal = false;
  exportFileName = '';
  exportFileBlob: Blob | null = null;
  exportEmailSubject = '';
  exportEmailBody = '';

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private commissionService: CommissionReportsService,
    private selectedDnService: SelectedDnService,
    private reportService: ReportService,
    private reportPageContext: ReportPageContextService,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    // Inicia com loading ativo
    this.loading = true;

    // Carrega as datas de fechamento em paralelo
    this.loadClosedDates();

    // Subscreve às mudanças do DN
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (dn) {
        // Recarregar dados quando DN mudar
        this.loadExercicioMaisRecente();
        // Forçar detecção de mudanças
        this.cdr.detectChanges();
      }
    });
  }

  loadClosedDates(): void {
    // Faz duas chamadas em paralelo, independentes
    // Cada uma atualiza sua variável assim que retornar

    // Chamada 1: Commission Performance
    this.reportService.getClosedDate('Commission Performance').subscribe({
      next: (date: string) => {
        this.closedDateCommissionPerformance = date || '';
      },
      error: (error) => {
        console.error('Erro ao carregar data de fechamento Commission Performance:', error);
        this.closedDateCommissionPerformance = '';
      }
    });

    // Chamada 2: Matriz ISR x Penetration
    this.reportService.getClosedDate('Matriz ISR x Penetration').subscribe({
      next: (date: string) => {
        this.closedDateMatrizIsr = date || '';
      },
      error: (error) => {
        console.error('Erro ao carregar data de fechamento Matriz ISR x Penetration:', error);
        this.closedDateMatrizIsr = '';
      }
    });
  }

  ngOnDestroy(): void {
    // Limpa a subscription para evitar memory leaks
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  // MÉTODO PARA LIDAR COM MUDANÇAS DE PERÍODO
  onPeriodChange(period: { year: number; month: number }): void {
    // Garante que só meses ímpares sejam aceitos
    if (period.month % 2 === 0) {
      runtimeLogger.warn('Apenas meses ímpares são permitidos');
      return;
    }

    // Verifica se o período já está selecionado (evita recarregamento desnecessário)
    if (this.selectedYear === period.year && this.selectedMonth === period.month) {
      return;
    }

    this.selectedYear = period.year;
    this.selectedMonth = period.month;
    this.loadDataWithCurrentPeriod();
  }

  // No TypeScript, mudar o parâmetro:
  onExportRequested(): void {
    const el = this.reportContainer?.nativeElement;
    if (!el) return;

    const pageTitle = typeof this.reportPageContext?.getTitle === 'function'
      ? this.reportPageContext.getTitle()
      : 'relatorio-comissao';

    html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const safeTitle = pageTitle.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '') || 'relatorio-comissao';
      const fileName = `${safeTitle}-${this.selectedYear}-${this.selectedMonth}.pdf`;
      pdf.save(fileName);
    }).catch((err) => console.error('Erro ao gerar PDF:', err));
  }

  onExportCancelled(): void {
    console.log('Exportação cancelada');
  }

  private getShortMonthName(month: number): string {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return monthNames[month - 1] || '';
  }

  // CARREGAR DADOS COM O PERÍODO ATUAL
  private loadDataWithCurrentPeriod(): void {
    if (!this.selectedMonth || !this.selectedYear) {
      runtimeLogger.warn('Nenhum mês ou ano selecionado');
      return;
    }

    // Garante que o mês é ímpar (segurança extra)
    if (this.selectedMonth % 2 === 0) {
      runtimeLogger.warn('Tentando carregar com mês par, ajustando para ímpar');
      this.selectedMonth = this.selectedMonth - 1;
      if (this.selectedMonth < 1) {
        this.selectedMonth = 11;
        this.selectedYear = this.selectedYear - 1;
      }
    }

    const exercicio = parseInt(`${this.selectedYear}${this.selectedMonth.toString().padStart(2, '0')}`, 10);
    runtimeLogger.debug(`Carregando dados para exercício: ${exercicio} (${this.selectedYear}-${this.selectedMonth})`);

    this.loadAllDataComDNsDiferentes(exercicio);
  }

  /**
   * Carrega dados usando exatamente os valores que funcionam nos testes
   */
  private loadExercicioMaisRecente(): void {
    this.loadAllDataComDNsDiferentes();
  }

  /**
   * Carrega dados usando DNs diferentes para cada API (conforme testes)
   */
  private loadAllDataComDNsDiferentes(exercicio?: number): void {
    this.loading = true;
    this.exercicioMismatchError = null;
    this.errorMessage = null;
    this.is404Error = false;

    const dnApi1 = this.selectedDnService.getSelectedDnAsNumber();
    // Se não há exercício selecionado, tenta usar o primeiro de availableExercicios ou um padrão
    let exercicioApi1: number;
    if (exercicio) {
      exercicioApi1 = exercicio;
    } else if (this.selectedYear && this.selectedMonth) {
      exercicioApi1 = parseInt(`${this.selectedYear}${this.selectedMonth.toString().padStart(2, '0')}`, 10);
    } else if (this.availableExercicios && this.availableExercicios.length > 0) {
      // Usa o primeiro exercício disponível
      const firstExercicio = this.availableExercicios[0];
      exercicioApi1 = parseInt(firstExercicio, 10);
    } else {
      // Fallback temporário - será ajustado quando os dados retornarem
      exercicioApi1 = 202511;
    }
    const dnApi2 = this.selectedDnService.getSelectedDnAsNumber();
    const exercicioApi2 = exercicioApi1;

    forkJoin({
      reports: this.commissionService.getCommissionReports(exercicioApi1, dnApi1),
      navalMatrix: this.commissionService.getNavalMatriz(exercicioApi2, dnApi2)
    }).subscribe({
      next: ({ reports, navalMatrix }) => {

        if (!reports || !reports.data) {
          console.error('❌ API 1 retornou dados vazios ou null');
          this.loading = false;
          return;
        }

        if (!navalMatrix || navalMatrix.length === 0) {
          console.error('❌ API 2 retornou array vazio ou null');
          this.loading = false;
          return;
        }

        // Extrai availableExercicios da resposta
        this.availableExercicios = reports.availableExercicios || [];

        this.reportsData = reports.data;
        this.exercicioAtual = reports.data.exercicio;

        this.mapReportsData(reports.data);

        this.mapApiDataToTable(navalMatrix, reports.data);

        this.initCharts();

        this.loading = false;
        this.errorMessage = null;
        this.is404Error = false;
        // Forçar detecção de mudanças após carregar dados
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('❌ ERRO ao carregar dados:', error);
        this.loading = false;

        // Verifica se é erro 404
        if (error?.status === 404) {
          this.is404Error = true;
          this.errorMessage = 'Informação não encontrada para o exercício selecionado!';
          this.exercicioMismatchError = null;
        } else {
          this.is404Error = false;
          this.errorMessage = 'Ocorreu um erro no processamento da requisição! Tente novamente mais tarde e/ou procure um administrador!';
          this.exercicioMismatchError = null;
        }
        this.cdr.detectChanges();
      }
    });
  }

  private mapReportsData(data: CommissionReportsData): void {
    if (!data) {
      console.error('Dados da API 1 estão vazios ou undefined');
      return;
    }

    // Mapear Índices Vigentes e Próximos
    this.varejoIndice = {
      top: { value: data.indiceVigenteVarejo != null ? this.formatPercentage(data.indiceVigenteVarejo) : '' },
      bottom: { value: data.indiceVigenteFrota != null ? this.formatPercentage(data.indiceVigenteFrota) : '' }
    };

    this.frotaIndice = {
      top: { value: data.indiceParaProximoVarejo != null ? this.formatPercentage(data.indiceParaProximoVarejo) : '' },
      bottom: { value: data.indiceParaProximoFrota != null ? this.formatPercentage(data.indiceParaProximoFrota) : '' }
    };

    // Mapear Período Atual
    const periodoAtual = data.periodoAtual ? data.periodoAtual.charAt(0).toUpperCase()
                                              + data.periodoAtual.slice(1) : ''; // Usa o período atual da API
    const periodoAnterior = data.exerFechAnt ? data.exerFechAnt.charAt(0).toUpperCase()
                                              + data.exerFechAnt.slice(1) : ''; // Usa o exercício fechamento anterior da API

    // VAREJO: Período Atual
    this.varejoPeriodoAtual = {
      periodo: periodoAtual,
      penetration: data.penetrationVarejo != null ? this.formatPercentage(data.penetrationVarejo) : '',
      isr: data.isrVarejo != null ? this.formatPercentage(data.isrVarejo) : ''
    };

    // FROTA: Período Atual
    this.frotaPeriodoAtual = {
      periodo: periodoAtual,
      penetration: data.penetrationFrota != null ? this.formatPercentage(data.penetrationFrota) : '',
      isr: data.isrFrota != null ? this.formatPercentage(data.isrFrota) : ''
    };

    // CORREÇÃO: VAREJO - Período Anterior (Mês 1)
    this.varejoPeriodoAnterior = {
      periodo: periodoAnterior,
      penetration: data.penetrationVarejoAnt != null ? this.formatPercentage(data.penetrationVarejoAnt) : '',
      isr: data.isrVarejoAnt != null ? this.formatPercentage(data.isrVarejoAnt) : ''
    };

    // CORREÇÃO: FROTA - Período Anterior (Mês 1)
    this.frotaPeriodoAnterior = {
      periodo: periodoAnterior,
      penetration: data.penetrationFrotaAnt != null ? this.formatPercentage(data.penetrationFrotaAnt) : '',
      isr: data.isrFrotaAnt != null ? this.formatPercentage(data.isrFrotaAnt) : ''
    };

    // Mapear Métricas de Comparação
    const concessionariaNome = data.nmGrpComission || 'Euroaméricas';

    this.varejoMetrics = {
      penetration: [
        { label: 'Nacional', value: data.penVarejoBra != null ? this.formatPercentage(data.penVarejoBra) : '', color: 'dark-blue' },
        { label: 'Regional', value: data.penVarejoReg != null ? this.formatPercentage(data.penVarejoReg) : '', color: 'light-gray' },
        { label: concessionariaNome, value: data.penVarejoDn != null ? this.formatPercentage(data.penVarejoDn) : '', color: 'light-green' }
      ],
      isr: [
        { label: 'Nacional', value: data.isrVarejoBra != null ? this.formatPercentage(data.isrVarejoBra) : '', color: 'light-gray' },
        { label: 'Regional', value: data.isrVarejoReg != null ? this.formatPercentage(data.isrVarejoReg) : '', color: 'light-gray' },
        { label: concessionariaNome, value: data.isrVarejoDn != null ? this.formatPercentage(data.isrVarejoDn) : '', color: 'light-green' }
      ]
    };

    this.frotaMetrics = {
      penetration: [
        { label: 'Nacional', value: data.penFrotaBrasil != null ? this.formatPercentage(data.penFrotaBrasil) : '', color: 'dark-blue' },
        { label: 'Regional', value: data.penFrotaReg != null ? this.formatPercentage(data.penFrotaReg) : '', color: 'light-gray' },
        { label: concessionariaNome, value: data.penFrotaDn != null ? this.formatPercentage(data.penFrotaDn) : '', color: 'light-green' }
      ],
      isr: [
        { label: 'Nacional', value: data.isrFrotaBrasil != null ? this.formatPercentage(data.isrFrotaBrasil) : '', color: 'light-gray' },
        { label: 'Regional', value: data.isrFrotaReg != null ? this.formatPercentage(data.isrFrotaReg) : '', color: 'light-gray' },
        { label: concessionariaNome, value: data.isrFrotaDn != null ? this.formatPercentage(data.isrFrotaDn) : '', color: 'light-green' }
      ]
    };
  }

  private mapApiDataToTable(apiData: NavalMatrizItem[], reportsData?: CommissionReportsData): void {
    const varejoData = apiData.filter(item => item.tipo === 'VAREJO');
    const frotaData = apiData.filter(item => item.tipo === 'FROTA');

    this.varejoTableData = this.mapSegmentDataToTable(
      varejoData,
      reportsData?.penetrationVarejo,
      reportsData?.isrVarejo
    );

    this.frotaTableData = this.mapSegmentDataToTable(
      frotaData,
      reportsData?.penetrationFrota,
      reportsData?.isrFrota
    );
  }

  private mapSegmentDataToTable(
    segmentData: NavalMatrizItem[],
    currentPenetration?: number,
    currentISR?: number
  ): any[] {
    // Atualizado para incluir todas as faixas da interface
    const faixaMapping = [
      'faixa_0_39_9',
      'faixa_40_49_9',
      'faixa_50_54_9',
      'faixa_55_59_9',
      'faixa_60_64_9',
      'faixa_65_69_9',
      'faixa_70_74_9',
      'faixa_75_79_9',
      'faixa_80_84_9',
      'faixa_85_89_9',
      'faixa_90_99_9',
      'faixa_100_124_9',
      'faixa_125_149_9',
      'faixa_150_174_9',
      'faixa_175_199_9',
      'faixa_Maior_200'
    ];

    const getISRColumnIndex = (isr: number): number | null => {
      if (isr >= 0 && isr < 40) return 0;
      if (isr >= 40 && isr < 50) return 1;
      if (isr >= 50 && isr < 55) return 2;
      if (isr >= 55 && isr < 60) return 3;
      if (isr >= 60 && isr < 65) return 4;
      if (isr >= 65 && isr < 70) return 5;
      if (isr >= 70 && isr < 75) return 6;
      if (isr >= 75 && isr < 80) return 7;
      if (isr >= 80 && isr < 85) return 8;
      if (isr >= 85 && isr < 90) return 9;
      if (isr >= 90 && isr < 100) return 10;
      if (isr >= 100 && isr < 125) return 11;
      if (isr >= 125 && isr < 150) return 12;
      if (isr >= 150 && isr < 175) return 13;
      if (isr >= 175 && isr < 200) return 14;
      if (isr >= 200) return 15;
      return null;
    };

    const getPenetrationRow = (penetration: number, rows: any[]): number | null => {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const range = row.range;

        if (range.includes('>')) {
          const match = range.match(/>\s*(\d+\.?\d*)/);
          if (match && penetration > parseFloat(match[1])) {
            return i;
          }
        } else if (range.includes('<')) {
          const match = range.match(/<\s*(\d+\.?\d*)/);
          if (match && penetration < parseFloat(match[1])) {
            return i;
          }
        } else {
          const match = range.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
          if (match) {
            const min = parseFloat(match[1]);
            const max = parseFloat(match[2]);
            if (penetration >= min && penetration <= max) {
              return i;
            }
          }
        }
      }
      return null;
    };

    const sortedData = [...segmentData].sort((a, b) => {
      const getPenetrationValue = (pen: string): number => {
        if (pen.includes('>')) {
          return 999;
        }
        if (pen.includes('<')) {
          return -1;
        }
        const match = pen.match(/(\d+\.?\d*)/);
        return match ? parseFloat(match[1]) : 0;
      };
      return getPenetrationValue(b.penetration) - getPenetrationValue(a.penetration);
    });

    const rows = sortedData.map(item => {
      const row: any = {
        range: item.penetration,
        highlighted: undefined,
        specialChar: undefined,
        specialIcon: undefined
      };

      // Mapeia todas as faixas para as colunas
      faixaMapping.forEach((faixa, index) => {
        const value = item[faixa as keyof NavalMatrizItem] as number;
        row['col' + (index + 1)] = this.formatDecimalAsPercentage(value);
      });

      return row;
    });

    if (currentPenetration !== undefined && currentISR !== undefined) {
      const rowIndex = getPenetrationRow(currentPenetration, rows);
      const colIndex = getISRColumnIndex(currentISR);

      if (rowIndex !== null && colIndex !== null) {
        rows[rowIndex].highlighted = colIndex;
      }
    }

    return rows;
  }

  private formatPercentage(value: number): string {
    return value.toFixed(2).replace('.', ',') + '%';
  }

  private formatDecimalAsPercentage(value: number): string {
    const percentage = value.toFixed(2);
    return percentage.replace('.', ',') + '%';
  }

  selectSegment(segment: 'varejo' | 'frota'): void {
    this.selectedSegment = segment;
  }

  private initCharts(): void {
    if (!this.reportsData) {
      this.varejoPerformanceChart = this.getPerformanceChart('varejo', [], [], []);
      this.frotaPerformanceChart = this.getPerformanceChart('frota', [], [], []);
      return;
    }

    // Dados de Varejo - ORDEM CRONOLÓGICA (do mais antigo para o mais recente)
    const varejoData = {
      penetration: [
        this.reportsData.penMes6Varejo, // Mais antigo
        this.reportsData.penMes5Varejo,
        this.reportsData.penMes4Varejo,
        this.reportsData.penMes3Varejo,
        this.reportsData.penMes2Varejo,
        this.reportsData.penMes1Varejo  // Mais recente
      ],
      isr: [
        this.reportsData.isrMes6Varejo, // Mais antigo
        this.reportsData.isrMes5Varejo,
        this.reportsData.isrMes4Varejo,
        this.reportsData.isrMes3Varejo,
        this.reportsData.isrMes2Varejo,
        this.reportsData.isrMes1Varejo  // Mais recente
      ],
      meses: [
        this.reportsData.nomeMes6, // Mais antigo
        this.reportsData.nomeMes5,
        this.reportsData.nomeMes4,
        this.reportsData.nomeMes3,
        this.reportsData.nomeMes2,
        this.reportsData.nomeMes1  // Mais recente
      ]
    };

    // Dados de Frota - ORDEM CRONOLÓGICA (do mais antigo para o mais recente)
    const frotaData = {
      penetration: [
        this.reportsData.penMes6Frota, // Mais antigo
        this.reportsData.penMes5Frota,
        this.reportsData.penMes4Frota,
        this.reportsData.penMes3Frota,
        this.reportsData.penMes2Frota,
        this.reportsData.penMes1Frota  // Mais recente
      ],
      isr: [
        this.reportsData.isrMes6Frota, // Mais antigo
        this.reportsData.isrMes5Frota,
        this.reportsData.isrMes4Frota,
        this.reportsData.isrMes3Frota,
        this.reportsData.isrMes2Frota,
        this.reportsData.isrMes1Frota  // Mais recente
      ],
      meses: [
        this.reportsData.nomeMes6, // Mais antigo
        this.reportsData.nomeMes5,
        this.reportsData.nomeMes4,
        this.reportsData.nomeMes3,
        this.reportsData.nomeMes2,
        this.reportsData.nomeMes1  // Mais recente
      ]
    };

    this.varejoPerformanceChart = this.getPerformanceChart('varejo', varejoData.meses, varejoData.penetration, varejoData.isr);
    this.frotaPerformanceChart = this.getPerformanceChart('frota', frotaData.meses, frotaData.penetration, frotaData.isr);
  }

  private getPerformanceChart(
    segment: 'varejo' | 'frota',
    meses: string[],
    penetrationData: number[],
    isrData: number[]
  ): EChartsOption {
    const mesesLabels = meses.length > 0 ? meses : ['Mês 6', 'Mês 5', 'Mês 4', 'Mês 3', 'Mês 2', 'Mês 1'];
    const penData = penetrationData.length > 0 ? penetrationData : [0, 0, 0, 0, 0, 0];
    const isrDataValues = isrData.length > 0 ? isrData : [0, 0, 0, 0, 0, 0];

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
        formatter: (params: any) => {
          let result = params[0].name + '<br/>';
          params.forEach((param: any) => {
            result += `${param.marker}${param.seriesName}: ${param.value}%<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: ['Penetration', 'ISR'],
        bottom: 0,
        textStyle: { fontSize: 12, color: '#4A4A4A' },
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20
      },
      grid: {
        left: '5%',
        right: '5%',
        top: '15%',
        bottom: '15%',
        containLabel: false
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: mesesLabels,
        axisLabel: {
          color: '#6A767D',
          fontSize: 11,
          interval: 0
        },
        axisLine: {
          lineStyle: { color: '#E0E0E0' }
        },
        splitLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLabel: {
          color: '#6A767D',
          fontSize: 11,
          show: false
        },
        axisLine: {
          show: false
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E0E0E0'
          }
        }
      },
      series: [
        {
          name: 'Penetration',
          type: 'line',
          smooth: true,
          lineStyle: { color: '#001E50', width: 2 },
          itemStyle: {
            color: '#001E50',
            borderColor: '#fff',
            borderWidth: 2
          },
          symbol: 'circle',
          symbolSize: 12,
          data: penData,
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => `${params.value}%`,
            color: '#001E50',
            fontSize: 11,
            fontWeight: 500
          }
        },
        {
          name: 'ISR',
          type: 'line',
          smooth: true,
          lineStyle: { color: '#6A767D', width: 2 },
          itemStyle: {
            color: '#6A767D',
            borderColor: '#fff',
            borderWidth: 2
          },
          symbol: 'circle',
          symbolSize: 12,
          data: isrDataValues,
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => `${params.value}%`,
            color: '#6A767D',
            fontSize: 11,
            fontWeight: 500
          }
        }
      ]
    };
  }

  onChartInit(ec: any): void {
    // Chart instance handler
  }

  getTableData(): any[] {
    return this.selectedSegment === 'varejo' ? this.varejoTableData : this.frotaTableData;
  }

  getIndiceData(): any {
    return this.selectedSegment === 'varejo' ? this.varejoIndice : this.frotaIndice;
  }

  getMetricsData(): any {
    return this.selectedSegment === 'varejo' ? this.varejoMetrics : this.frotaMetrics;
  }

  getPerformanceChartData(): EChartsOption {
    return this.selectedSegment === 'varejo' ? this.varejoPerformanceChart : this.frotaPerformanceChart;
  }

  getPeriodoAtualData(): any {
    return this.selectedSegment === 'varejo' ? this.varejoPeriodoAtual : this.frotaPeriodoAtual;
  }

  getPeriodoAnteriorData(): any {
    return this.selectedSegment === 'varejo' ? this.varejoPeriodoAnterior : this.frotaPeriodoAnterior;
  }

  isCellHighlighted(row: any, colIndex: number): boolean {
    return row.highlighted === colIndex;
  }

  isSpecialCharCell(row: any, colIndex: number): boolean {
    return row.specialChar === colIndex;
  }

  isSpecialIconCell(row: any, colIndex: number): boolean {
    return row.specialIcon === colIndex;
  }

  getCellValue(row: any, colIndex: number): string {
    return row['col' + (colIndex + 1)];
  }

  handleExportModalDownload() {
    if (this.exportFileBlob && this.exportFileName) {
      const objectUrl = window.URL.createObjectURL(this.exportFileBlob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = this.exportFileName;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(objectUrl);
    }
    this.showExportModal = false;
  }

  // Exportação PDF e abertura do modal ao exportar
  exportPdfModal() {
    const el = this.reportContainer?.nativeElement;
    if (!el) return;
    const pageTitle = typeof this.reportPageContext?.getTitle === 'function'
      ? this.reportPageContext.getTitle()
      : 'relatorio-comissao';
    const y = this.selectedYear ?? new Date().getFullYear();
    const m = this.selectedMonth ?? new Date().getMonth() + 1;
    html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
      ignoreElements: (element) => {
        if (element.tagName === 'IFRAME') return true;
        const el = element as HTMLElement;
        if (el.closest?.('.type-picker-selector') || el.closest?.('.export-selector')) return true;
        return false;
      },
      onclone: (_clonedDoc, clonedElement) => {
        clonedElement.style.overflow = 'visible';
        clonedElement.style.display = 'block';
      },
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const fileName = `${pageTitle}-${y}-${m}.pdf`;
      const blob = pdf.output('blob');
      this.exportFileName = fileName;
      this.exportFileBlob = blob;
      this.exportEmailSubject = `Relatório Comissão - ${y}/${m}`;
      this.exportEmailBody = `Segue em anexo o relatório de comissão referente ao período ${m}/${y}.`;
      this.showExportModal = true;
    }).catch((err) => console.error('Erro ao gerar PDF:', err));
  }
}
